import { useApiClient } from '@m43/core';
import {
    HStack,
    Icon,
    useToast,
    Flex,
    cssVar,
    Box,
    Text,
    Popover,
    PopoverAnchor,
    PopoverContent,
    styled,
} from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Descendant, Transforms, createEditor } from 'slate';
import { DefaultLeaf, Editable, ReactEditor, RenderLeafProps, Slate, withReact } from 'slate-react';
import { LoadingIndicator } from '../common/components/loading-indicator';
import { useEnabledFeatures } from '../common/hooks/enabled-features';
import {
    ActionHandler,
    useIntegratedActionBroadcaster,
} from '../action-broadcaster/use-integrated-action-broadcaster';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { useOpenEventPanel } from '../event-panel/use-open-event-panel';
import { useViewLocationOnMap } from '../location/hooks/use-view-location-on-map';
import { useLocationApi } from '../location-select/hooks/use-location-api';
import { useOpenWorkspacePanel } from '../panels/use-open-workspace-panel';
import { useOpenUnitHistoryPanel } from '../unit-history/hooks/use-open-unit-history-panel';
import { CommandLineDebug } from './command-line-debug';
import { CommandLineEditorStore } from './command-line-editor-store';
import { CommandLineHelpers } from './command-line-helpers';
import { CommandLineHints } from './command-line-hints';
import { CommandLineHistory } from './command-line-history';
import { CommandLineStashedCommands } from './command-line-stashed-commands';
import { SegmentTypeEnumType, segmentTypes } from './constants';
import { CommandLineConfigView } from './helpers';
import ValidationError from './types/validation-error';
import useDimensions from 'react-cool-dimensions';
import { useWorkspaceStore } from '../panels/use-workspace-store';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { WorkspacePanel } from '../panels/workspace-panel';
import { useUserActionTracker } from '../monitoring/user-action-tracker/use-user-action-tracker';
import { useDexNextForms } from '../panels/hooks/use-dex-next-forms';
import { useFetchDexMetadata } from '../data-exchange/use-dex-metadata-mutation';
import { colors } from '../colors/colors';

function withSingleLine(editor: ReactEditor) {
    const { normalizeNode } = editor;

    editor.normalizeNode = ([node, path]) => {
        if (path.length === 0) {
            if (editor.children.length > 1) {
                Transforms.mergeNodes(editor);
            }
        }

        return normalizeNode([node, path]);
    };

    return editor;
}

export const CommandLineEditor = observer(
    (props: { panel: WorkspacePanel; config: CommandLineConfigView }): React.ReactElement => {
        const { config, panel } = props;
        const panelHeight = panel.height;
        const panelWidth = panel.width;
        const panelIsFocused = panel.isFocused;
        const toast = useToast();
        const drawerState = useDrawerControls();
        const locationApi = useLocationApi();
        const apiClient = useApiClient();

        const openUnitHistoryPanel = useOpenUnitHistoryPanel();
        const openWorkspacePanel = useOpenWorkspacePanel();
        const enabledFeatures = useEnabledFeatures();

        const openEventPanel = useOpenEventPanel();
        const openEventPanelRef = useRef(openEventPanel);
        openEventPanelRef.current = openEventPanel;

        const viewLocationOnMap = useViewLocationOnMap();
        const viewLocationOnMapRef = useRef(viewLocationOnMap);
        viewLocationOnMapRef.current = viewLocationOnMap;

        const enabledFeaturesRef = useRef(enabledFeatures);
        enabledFeaturesRef.current = enabledFeatures;
        const { observe, height: historyHeight } = useDimensions();
        const { trigger } = useIntegratedActionBroadcaster();
        const { userProfileView, fieldDisplayNameByFieldName } = useBootstrapState();

        const userActionTracker = useUserActionTracker();
        const userActionTrackerRef = useRef(userActionTracker);
        userActionTrackerRef.current = userActionTracker;

        const dexNextForms = useDexNextForms();
        const dexNextFormsRef = useRef(dexNextForms);
        dexNextFormsRef.current = dexNextForms;

        const fetchDexMetadata = useFetchDexMetadata();
        const fetchDexMetadataRef = useRef(fetchDexMetadata);
        fetchDexMetadataRef.current = fetchDexMetadata;

        const [editorStore] = useState(() => {
            return new CommandLineEditorStore(
                withSingleLine(withReact(createEditor())),
                config,
                apiClient,
                {
                    dexStateUserId: userProfileView.dexStateUserId,
                    toast,
                    locationApi,
                    drawerState,
                    openUnitHistoryPanel,
                    openWorkspacePanel,
                    getOpenEventPanel: () => openEventPanelRef.current,
                    getViewLocationOnMap: () => viewLocationOnMapRef.current,
                    triggerActions: trigger,
                    fieldDisplayNameByFieldName,
                    getEnabledFeaturesConfig: () => ({
                        allowMultiStashCommandsInCli: true,
                        cliSelfInitiateCadNext: enabledFeaturesRef.current.cliSelfInitiateCadNext,
                        unitHistory: enabledFeaturesRef.current.unitHistory,
                        cadNextDisableLegacyCadAccess:
                            enabledFeaturesRef.current.cadNextDisableLegacyCadAccess,
                        preferNewEventsWindow: enabledFeaturesRef.current.preferNewEventsWindow,
                        disableLegacyMapAndSearches:
                            enabledFeaturesRef.current.disableLegacyMapAndSearches,
                        mapWindowEnabled: enabledFeaturesRef.current.mapWindowEnabled,
                        subpremiseSupport: enabledFeaturesRef.current.subpremiseSupport,
                        dexNextEnabled: enabledFeaturesRef.current.dexNextEnabled,
                    }),
                    getUserActionTrackerInstance: () => userActionTrackerRef.current,
                    getDexNextForms: () => dexNextFormsRef.current,
                    getFetchDexMetadata: () => fetchDexMetadataRef.current(),
                }
            );
        });

        useEffect(() => {
            editorStore?.fetchHistory();
        }, [editorStore]);

        const stashCommand = useCallback(
            (event) => {
                editorStore.moveCurrentToStash();
                editorStore.focus();
                userActionTrackerRef.current?.trackStashCommand(
                    {
                        stashTrigger: 'BUTTON',
                        stashedCount: editorStore.stashedItems.length,
                        lastStashed:
                            editorStore.stashedItems?.[editorStore.stashedItems.length - 1]
                                ?.commandText,
                    },
                    event
                );
            },
            [editorStore]
        );

        useEffect(() => {
            if (panelIsFocused) {
                editorStore.focus();

                editorStore.moveToEnd();
            }
        }, [panelIsFocused, editorStore]);

        const handleActions: ActionHandler<'EVENT_V2' | 'QUEUE_V2'> = useCallback(
            (action) => {
                if (action.type === 'FOCUS_COMMAND_LINE_PANEL') {
                    editorStore.focus();
                    return true;
                }
            },
            [editorStore]
        );

        // Since this broadcast listener is to be used in a panel that can show up in _any_ window,
        // We have to dynamically determine the window type we want to listen on for actions
        const workspacePanelsStore = useWorkspaceStore();
        const activeWorkspaceWindowType = workspacePanelsStore.activeWorkspaceWindowType;
        if (activeWorkspaceWindowType !== 'EVENT_V2' && activeWorkspaceWindowType !== 'QUEUE_V2') {
            throw new Error('Unexpectedly did not fine active workspace window type');
        }
        useIntegratedActionBroadcaster({
            windowType: activeWorkspaceWindowType,
            handler: handleActions,
        });

        const currentSegment = editorStore.getCurrentSegment;
        const currentSegmentType = currentSegment ? currentSegment.segmentType : undefined;
        const activeCommandSegment = editorStore.activeCommandSegment;

        const renderLeaf = useCallback((props: RenderLeafProps) => {
            // @ts-expect-error Property 'type' does not exist on type 'CustomText'.
            if (props.leaf.type) {
                // @ts-expect-error Property 'type' does not exist on type 'CustomText'
                switch (props.leaf.type as SegmentTypeEnumType) {
                    case segmentTypes.LOCATION: {
                        const location = editorStore.savedLocations.get(
                            props.leaf.text.toUpperCase()
                        );

                        const verified = !!location?.latitude && !!location?.longitude;

                        let color = cssVar('arc.colors.text.primary');

                        if (location) {
                            color = verified
                                ? colors.commandLineLocationVerified
                                : colors.commandLineLocationUnverified;
                        }

                        return (
                            <span {...props.attributes} style={{ color }}>
                                {props.children}
                            </span>
                        );
                    }
                    case segmentTypes.EVENT:
                        return (
                            <span
                                style={{ color: cssVar('arc.colors.text.primary') }}
                                {...props.attributes}
                            >
                                {props.children}
                            </span>
                        );
                    case segmentTypes.COMMAND:
                        return <span {...props.attributes}>{props.children}</span>;
                    case segmentTypes.DISPOSITION:
                    case segmentTypes.DISPATCH_AREA:
                    case segmentTypes.DISPATCH_AREA_GROUP:
                    case segmentTypes.MEMBER:
                    case segmentTypes.MESSAGE_GROUP:
                    case segmentTypes.UNIT_TAG:
                    case segmentTypes.CALL_TAKER_STATION:
                    case segmentTypes.VEHICLE:
                    case segmentTypes.CALL_FOR_SERVICE:
                    case segmentTypes.AGENCY:
                    case segmentTypes.MESSAGE_RECIPIENT:
                        return <span {...props.attributes}>{props.children}</span>;
                    case segmentTypes.COMMENT:
                        return (
                            <span
                                style={{
                                    color: colors.commandLineLeafComment,
                                    fontStyle: 'italic',
                                }}
                                {...props.attributes}
                            >
                                {props.children}
                            </span>
                        );
                    default:
                        return <DefaultLeaf {...props} />;
                }
            }

            return <DefaultLeaf {...props} />;
        }, []); // eslint-disable-line react-hooks/exhaustive-deps

        const value: Descendant[] = [
            { type: 'paragraph', children: [{ text: editorStore.currentText }] },
        ];

        //TODO: Get dynamically
        const stashedMaxHeight = panelHeight - (50 + 21 + 42);

        return (
            <>
                <Box className="history-stashed-wrapper" h="full" flexDir="column" display="flex">
                    <Box flex={1}>
                        <CommandLineHistory editorStore={editorStore} historyRef={observe} />
                    </Box>

                    {editorStore.stashedItems.length ? (
                        <Box flex={1} flexGrow={0} maxH={stashedMaxHeight}>
                            <CommandLineStashedCommands editorStore={editorStore} />
                        </Box>
                    ) : null}
                </Box>
                <Popover isOpen={!!editorStore.helperState?.activeHelper} hasArrow={false}>
                    <div style={{ position: 'relative' }}>
                        <PopoverAnchor>
                            <Box
                                h="50px"
                                borderTop="md"
                                borderBottom="md"
                                borderColor={
                                    editorStore.validationError
                                        ? '2px solid var(--arc-colors-raw-red-600)'
                                        : editorStore.helperState?.activeHelper
                                        ? '2px solid rgb(83, 234, 200)'
                                        : '2px solid white'
                                }
                            >
                                <HStack h="100%">
                                    <Flex
                                        w="40px"
                                        height="50px"
                                        justifyContent="center"
                                        alignItems="center"
                                        color="text.primary"
                                    >
                                        <svg
                                            width="12"
                                            height="16"
                                            viewBox="0 0 12 16"
                                            fill="currentColor"
                                        >
                                            <path d="M 12 8 L 0 0 L 0 16 Z" />
                                        </svg>
                                    </Flex>
                                    <Slate
                                        editor={editorStore.editor}
                                        value={value}
                                        onChange={editorStore.onChange}
                                    >
                                        <Editable
                                            autoFocus
                                            onKeyDown={editorStore.onKeyDown}
                                            onKeyUp={editorStore.onKeyUp}
                                            renderLeaf={renderLeaf}
                                            decorate={editorStore.decorate}
                                            style={{
                                                flexGrow: 1,
                                                lineHeight: '50px',
                                                height: '50px',
                                                fontFamily: 'roboto-mono',
                                                textTransform: 'uppercase',
                                                fontSize: '32px',
                                                fontWeight: '300',
                                                overflow: 'hidden',
                                                whiteSpace: 'pre',
                                            }}
                                        />
                                    </Slate>
                                    {editorStore.isAutocompleteLoading && (
                                        <Box
                                            w="100px"
                                            height="50px"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            border="none"
                                        >
                                            <LoadingIndicator />
                                        </Box>
                                    )}
                                    <Box
                                        onClick={(event) => stashCommand(event)}
                                        cursor="pointer"
                                        w="48px"
                                        height="50px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        borderLeft="md"
                                    >
                                        <Icon icon="Add" size="lg" />
                                    </Box>
                                </HStack>
                            </Box>
                        </PopoverAnchor>
                        <HStack px="2" h="21px">
                            <Text variant="bodyMd" isTruncated noOfLines={1}>
                                {activeCommandSegment &&
                                currentSegmentType !== segmentTypes.LOCATION ? ( // location aliases / subdivisions go in this sopt
                                    <span>{activeCommandSegment?.segmentData.title}</span>
                                ) : null}
                            </Text>
                            <CommandLineHints
                                config={config}
                                activeCommandSegment={editorStore.activeCommandSegment}
                            />
                        </HStack>
                        {editorStore.validationError ? (
                            <ValidationError message={editorStore.validationError} />
                        ) : null}
                        <StyledPopoverContent
                            collisionPadding={0}
                            onOpenAutoFocus={(e) => e.preventDefault()}
                            side="top"
                            hasPadding={false}
                            style={{ width: panelWidth, backgroundColor: 'unset', border: 'unset' }}
                            sideOffset={-2}
                            alignOffset={0}
                        >
                            <CommandLineHelpers
                                panel={panel}
                                historyHeight={historyHeight}
                                locationApi={locationApi}
                                editorStore={editorStore}
                            />
                        </StyledPopoverContent>
                    </div>

                    {process.env.NODE_ENV === 'development' && editorStore.debugOpen ? (
                        <CommandLineDebug editorStore={editorStore} />
                    ) : null}
                </Popover>
            </>
        );
    }
);

// Depending on the type of popover, the width of the content in `CommandLineHelpers` is smaller than
// the popover. In light mode, there is a shadow that appears at the natural full width, so it looks funny.
// Here, we hide the shadow.
const StyledPopoverContent = styled(PopoverContent)`
    box-shadow: unset;
`;
